/**
 * AIBubbleMenu - Dropdown menu for AI text transformations
 *
 * Shows when user clicks AI button in BubbleToolbar.
 * Provides options to transform selected text using AI:
 * - Rewrite: Rephrase in different words
 * - Summarize: Make it shorter and concise
 * - Expand: Add more detail and depth
 * - Simplify: Make it easier to understand
 * - Formal: Professional tone
 * - Casual: Conversational tone
 * - Professional: Business-appropriate language
 *
 * The actual transformation is handled by the parent component
 * which sends the request to the Board Durable Object via WebSocket.
 *
 * @module components/editor/AIBubbleMenu
 */

import { useCallback, useState } from 'react';
import type { Editor } from '@tiptap/react';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Props for AIBubbleMenu component.
 */
interface AIBubbleMenuProps {
  /** Tiptap editor instance to get selected text */
  editor: Editor;
  /** Whether the dropdown menu is currently visible */
  isOpen: boolean;
  /** Callback when the menu should close (click outside, after selection, etc.) */
  onClose: () => void;
  /** Callback when user selects a transform action */
  onTransform: (action: AITransformAction, selectedText: string) => void;
}

/**
 * Available AI text transformation actions.
 *
 * These map to prompts in the Board Durable Object that guide
 * the AI on how to transform the selected text.
 */
export type AITransformAction =
  | 'rewrite'
  | 'summarize'
  | 'expand'
  | 'simplify'
  | 'formal'
  | 'casual'
  | 'professional';

/**
 * Configuration for each transform option shown in the dropdown.
 */
interface TransformOption {
  /** Unique identifier matching AITransformAction */
  id: AITransformAction;
  /** User-facing label for the option */
  label: string;
  /** Short description explaining what this transform does */
  description: string;
  /** Emoji icon for visual identification */
  icon: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * All available AI transform options.
 *
 * Order determines display order in the dropdown menu.
 * Each option includes an icon, label, and description to help
 * users understand what transformation will be applied.
 */
const AI_TRANSFORM_OPTIONS: TransformOption[] = [
  {
    id: 'rewrite',
    label: 'Rewrite',
    description: 'Rephrase in different words',
    icon: '🔄',
  },
  {
    id: 'summarize',
    label: 'Summarize',
    description: 'Make it shorter and concise',
    icon: '📝',
  },
  {
    id: 'expand',
    label: 'Expand',
    description: 'Add more detail and depth',
    icon: '📖',
  },
  {
    id: 'simplify',
    label: 'Simplify',
    description: 'Make it easier to understand',
    icon: '✨',
  },
  {
    id: 'formal',
    label: 'Make Formal',
    description: 'Professional tone',
    icon: '👔',
  },
  {
    id: 'casual',
    label: 'Make Casual',
    description: 'Conversational tone',
    icon: '💬',
  },
  {
    id: 'professional',
    label: 'Professional',
    description: 'Business-appropriate language',
    icon: '💼',
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * AIBubbleMenu component - Dropdown menu for AI text transformations.
 *
 * Renders as an absolutely-positioned dropdown that appears below the AI button
 * in the BubbleToolbar. Each option button triggers an AI transformation request.
 *
 * The component shows a loading spinner while a transformation is in progress
 * to provide feedback that the AI is working.
 *
 * @example
 * ```tsx
 * <AIBubbleMenu
 *   editor={editor}
 *   isOpen={showAIMenu}
 *   onClose={() => setShowAIMenu(false)}
 *   onTransform={handleAITransform}
 * />
 * ```
 */
export function AIBubbleMenu({
  editor,
  isOpen,
  onClose,
  onTransform,
}: AIBubbleMenuProps) {
  // Track whether a transformation is in progress
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Handle clicking on a transform option.
   *
   * 1. Gets the currently selected text from the editor
   * 2. Validates that text is actually selected
   * 3. Calls the onTransform callback with the action and selected text
   * 4. Closes the menu when done
   *
   * The isProcessing state shows a spinner during the async operation.
   */
  const handleTransform = useCallback(
    async (action: AITransformAction) => {
      // Get the current selection range from the editor
      const { from, to } = editor.state.selection;
      // Extract the plain text between the selection points
      const selectedText = editor.state.doc.textBetween(from, to);

      // Don't proceed if nothing is selected (or only whitespace)
      if (!selectedText.trim()) {
        onClose();
        return;
      }

      // Show loading state
      setIsProcessing(true);

      try {
        // Call the transform handler (passed from parent)
        // This will send the request to the Board DO via WebSocket
        await onTransform(action, selectedText);
      } finally {
        // Always reset processing state and close menu
        setIsProcessing(false);
        onClose();
      }
    },
    [editor, onTransform, onClose]
  );

  // Don't render anything if the menu is closed
  if (!isOpen) return null;

  return (
    <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[200px] z-50">
      {/* Show spinner when processing a transformation */}
      {isProcessing ? (
        <div className="px-4 py-3 text-center">
          {/* Spinning violet loader - matches AI theme color */}
          <div className="inline-block animate-spin w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full" />
          <p className="text-sm text-gray-500 mt-1">Transforming...</p>
        </div>
      ) : (
        /* Show transform options when not processing */
        AI_TRANSFORM_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => handleTransform(option.id)}
            className="w-full px-4 py-2 text-left hover:bg-violet-50 flex items-center gap-3 transition-colors"
          >
            {/* Icon */}
            <span className="text-lg">{option.icon}</span>
            {/* Label and description */}
            <div>
              <div className="text-sm font-medium text-gray-900">{option.label}</div>
              <div className="text-xs text-gray-500">{option.description}</div>
            </div>
          </button>
        ))
      )}
    </div>
  );
}
